import { useLocation, useNavigate } from 'react-router-dom'

import {
  ArrowLeftIcon,
  Button,
  ChannelAvatar,
  EmptyState,
  Flex,
  Link,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import {
  getInsightsChannelTypeLabel,
  SUPPORTED_CHANNELS_ARTICLE_URL,
} from '../utils'

import styles from './InsightsNotSupported.module.css'

import type { JSX } from 'react'

type ChannelInfo = {
  service: ChannelType
  type: string
  name: string
  displayName: string | null
}

export function InsightsNotSupported({
  channel,
  avatar,
}: {
  channel: ChannelInfo
  avatar?: string
}): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()

  // "Back" should return users to wherever they came from rather than always
  // landing on the Insights home — the channel filter can be reached from many
  // places. React Router seeds the first in-app entry with key 'default', so
  // that's our signal that there's no history to pop; fall back to /insights.
  const handleBack = (): void => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/insights')
    }
  }

  return (
    <Flex align="center" justify="center" fullHeight fullWidth>
      <EmptyState size="xlarge" variant="warning" className={styles.emptyState}>
        <ChannelAvatar
          channel={channel.service}
          src={avatar ?? undefined}
          alt={channel.name}
          size="large"
          className={styles.channelAvatar}
        />
        <EmptyState.Heading>
          Insights aren&apos;t available for{' '}
          {getInsightsChannelTypeLabel(channel.service, channel.type)}
        </EmptyState.Heading>
        <EmptyState.Description>
          This channel type doesn&apos;t support Insights yet. <br />
          <Link
            href={SUPPORTED_CHANNELS_ARTICLE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            More about supported channels
          </Link>
          .
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button variant="secondary" onClick={handleBack}>
            <ArrowLeftIcon /> Back
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    </Flex>
  )
}
